import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  Stack,
  Typography,
} from "@mui/joy";
import {
  AutoFixHigh as GenerateIcon,
  ExpandLess as CollapseIcon,
  ExpandMore as ExpandIcon,
} from "@mui/icons-material";
import { PaletteShades } from "../../../common/types";
import { generatePaletteFromHex, isValidHex } from "../../../theme/paletteGenerator";
import ColorInput from "./ColorInput";
import ShadeRow from "./ShadeRow";

const SHADE_KEYS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const;

interface PaletteEditorProps {
  label: string;
  value: PaletteShades | undefined;
  onChange: (shades: PaletteShades | undefined) => void;
  disabled?: boolean;
  defaultShades?: PaletteShades;
}

const PaletteEditor: React.FC<PaletteEditorProps> = ({
  label,
  value,
  onChange,
  disabled,
  defaultShades,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [brandHex, setBrandHex] = useState(value?.[500] ?? "");
  const [generateError, setGenerateError] = useState<string | null>(null);
  const palette = value ?? undefined;
  const hasOverrides = palette != null;

  useEffect(() => {
    setBrandHex(palette?.[500] ?? "");
  }, [palette]);

  const handleGenerate = () => {
    const hex = brandHex.trim().startsWith("#") ? brandHex.trim() : `#${brandHex.trim()}`;
    if (!isValidHex(hex)) {
      setGenerateError("Enter a valid hex colour (e.g. #3b82f6)");
      return;
    }
    setGenerateError(null);
    onChange(generatePaletteFromHex(hex));
    setExpanded(true);
  };

  const handleShadeChange = (shade: number, hex: string) => {
    if (!palette) return;
    onChange({ ...palette, [shade]: hex });
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography level="title-sm" sx={{ fontWeight: 600 }}>
          {label}
          {hasOverrides && (
            <Box
              component="span"
              sx={{
                ml: 1,
                px: 0.75,
                py: 0.1,
                borderRadius: "10px",
                bgcolor: "primary.100",
                color: "primary.600",
                fontSize: "10px",
                fontWeight: 600,
                verticalAlign: "middle",
              }}
            >
              custom
            </Box>
          )}
        </Typography>

        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {hasOverrides && (
            <Button
              size="sm"
              variant="plain"
              color="neutral"
              onClick={() => setExpanded((e) => !e)}
              endDecorator={expanded ? <CollapseIcon /> : <ExpandIcon />}
            >
              {expanded ? "Hide shades" : "Edit shades"}
            </Button>
          )}
        </Box>
      </Box>

      {/* Brand colour input + generate */}
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box sx={{ flex: 1 }}>
          <ColorInput
            value={brandHex}
            onChange={(hex) => {
              setBrandHex(hex);
              setGenerateError(null);
            }}
            disabled={disabled}
            placeholder={defaultShades?.[500]}
          />
        </Box>
        <Button
          size="sm"
          variant="outlined"
          startDecorator={<GenerateIcon />}
          onClick={handleGenerate}
          disabled={disabled || !brandHex.trim()}
        >
          Generate
        </Button>
        {hasOverrides && (
          <Button
            size="sm"
            variant="plain"
            color="neutral"
            onClick={() => {
              onChange(undefined as any);
              setBrandHex("");
              setExpanded(false);
            }}
            disabled={disabled}
          >
            Reset
          </Button>
        )}
      </Stack>

      {generateError && (
        <Alert color="danger" variant="soft" size="sm" sx={{ mt: 1 }}>
          {generateError}
        </Alert>
      )}

      {/* Preview strip */}
      {hasOverrides && (
        <Box sx={{ display: "flex", mt: 1, borderRadius: "6px", overflow: "hidden", height: 20 }}>
          {SHADE_KEYS.map((s) => (
            <Box key={s} sx={{ flex: 1, bgcolor: palette?.[s] ?? "transparent" }} />
          ))}
        </Box>
      )}

      {/* Per-shade overrides */}
      {hasOverrides && expanded && (
        <Box sx={{ mt: 1 }}>
          <Divider sx={{ mb: 1 }} />
          <Stack spacing={0}>
            {SHADE_KEYS.map((s) => (
              <ShadeRow
                key={s}
                label={String(s)}
                value={palette?.[s] ?? ""}
                onChange={(hex) => handleShadeChange(s, hex)}
                disabled={disabled}
                placeholder={defaultShades?.[s]}
              />
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default PaletteEditor;
